import {isFilled} from '@prismicio/client'
import {PrismicRichText} from '@prismicio/react'
import dynamic from 'next/dynamic'
import type {CustomerStoriesSliceV2Primary} from 'prismicio-types'
import {memo, useEffect, useRef, useState} from 'react'
import {useWindowSize} from 'react-use'
import {ButtonV2} from 'src/common/ButtonV2'
import {Container} from 'src/common/Container'
import {CustomerStoryCard} from 'src/common/CustomerStoryCard'
import type {Context} from 'src/common/types'
import {useFilteredCustomerStories} from './useGetCustomerStories'

const CategoryList = dynamic(
  // eslint-disable-next-line boundaries/dependencies -- grandfathered cross-slice import, predates the slice-boundaries rule
  () => import('slices/IntegrationsContainer/CategoryList').then((mod) => mod.CategoryList),
  {
    ssr: false,
  },
)

type CustomerStoriesV2Props = {
  slice: {
    primary: CustomerStoriesSliceV2Primary
    slice_type: string
    variation: string
  }
  context: Context
}

export const CustomerStoriesV2Variation = memo(function (props: CustomerStoriesV2Props) {
  const {width} = useWindowSize()
  const containerRef = useRef<HTMLDivElement>(null)
  const selectorRef = useRef<HTMLDivElement>(null)
  const [isShownAll, setIsShownAll] = useState(false)
  const [lessNumber, setLessNumber] = useState(3)
  const [activeCategory, setActiveCategory] = useState(0)

  const {selectedStories, availableCategories} = useFilteredCustomerStories(
    activeCategory,
    props.context.customerStories ?? [],
  )

  const transformedCategories = availableCategories.map((category) => ({
    category_name: category,
  }))

  // Reset show-all state and recalculate the initial card count when viewport width changes
  useEffect(() => {
    setIsShownAll(false)

    if (width < 640) {
      setLessNumber(3)
      return
    }

    setLessNumber(6)
  }, [width])

  return (
    <Container
      data-slice-type={props.slice.slice_type}
      data-slice-variation={props.slice.variation}
      className="px-5 !pt-12 max-md:pb-4 md:px-10 lg:px-5 lg:!pt-[96px] xl:px-0"
      containerRef={containerRef}
      backgroundColor="Grey/100"
    >
      <div className="grid-base">
        <PrismicRichText
          components={{
            heading2: ({children}) => (
              <h2 className="col-span-full text-32 font-medium leading-10" ref={selectorRef}>
                {children}
              </h2>
            ),
          }}
          field={props.slice.primary.stories_title}
        />

        <div className="col-span-full pt-2">
          <CategoryList
            categories={transformedCategories}
            selectedCategory={activeCategory}
            onClick={setActiveCategory}
            trackingData={{
              mktgId: 'customer-stories-category-button',
              dataType: 'customer-stories-category',
              pageName: props.context.pageUid || '',
            }}
          />
        </div>

        {selectedStories.length > 0 && (
          <section className="col-span-full grid grid-cols-subgrid gap-y-8 pt-4 md:gap-y-12 md:pt-8">
            <div className="col-span-full grid grid-cols-subgrid gap-6 md:gap-y-6">
              {selectedStories.slice(0, isShownAll ? undefined : lessNumber).map((customerStory, index) => (
                <CustomerStoryCard
                  customerStory={customerStory}
                  key={index + customerStory.id + customerStory.lang}
                  readMoreLabel={props.slice.primary.read_more_card_label}
                  index={index}
                  pageUid={props.context.pageUid || 'customer-stories'}
                />
              ))}
            </div>

            {!isShownAll && selectedStories.length > lessNumber && (
              <div className="col-span-full flex justify-center">
                <ButtonV2
                  variant="primary"
                  className="!h-12"
                  trackingData={{
                    mktgId: 'customer-stories-show-more',
                    location: 'customer-stories-container',
                    dataType: 'button',
                    pageName: props.context.pageUid || '',
                  }}
                  onClick={() => setIsShownAll(true)}
                >
                  {props.slice.primary.show_more_label}
                </ButtonV2>
              </div>
            )}

            <nav aria-hidden="true" className="hidden">
              {selectedStories.map((story) => {
                const parentPage = story.data.parent_page

                if (!isFilled.contentRelationship(parentPage) || !parentPage.uid || !story.uid) {
                  return null
                }

                return (
                  <a key={story.id} href={`/${parentPage.uid}/${story.uid}`}>
                    {story.data.company_name}
                  </a>
                )
              })}
            </nav>
          </section>
        )}
      </div>
    </Container>
  )
})
